<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ReplyBox from 'dashboard/components/widgets/conversation/ReplyBox.vue';
import InteractionsContactList from './components/InteractionsContactList.vue';
import InteractionThread from './components/InteractionThread.vue';
import { useContactInteractions } from './composables/useContactInteractions';
import { useInteractionComposer } from './composables/useInteractionComposer';

const props = defineProps({
  contactId: { type: [String, Number], default: 0 },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const currentUserId = useMapGetter('getCurrentUserID');
const contactGetter = useMapGetter('contacts/getContact');

const scrollContainer = ref(null);
const selectedContactId = computed(() => Number(props.contactId) || 0);
const selectedContact = computed(() =>
  selectedContactId.value ? contactGetter.value(selectedContactId.value) : {}
);

const {
  conversations,
  meta,
  isFetching,
  isLoadingOlder,
  hasError,
  hasOlderConversations,
  hasConversations,
  messagesCount,
  isLoadingEarlier,
  loadInitial,
  loadOlder,
  loadEarlierMessages,
} = useContactInteractions(selectedContactId, scrollContainer);

const {
  replyTarget,
  replyTargetInboxName,
  isActivated,
  isComposerActive,
  activate,
} = useInteractionComposer(conversations);

const summaryText = computed(() =>
  meta.value.count === 1
    ? t('CONTACT_INTERACTIONS.HEADER.SUMMARY_ONE')
    : t('CONTACT_INTERACTIONS.HEADER.SUMMARY_OTHER', {
        count: meta.value.count,
      })
);

const showEmptyThread = computed(
  () =>
    selectedContactId.value &&
    !isFetching.value &&
    !hasError.value &&
    !hasConversations.value
);

const onScroll = event => {
  if (event.target.scrollTop >= 80) return;
  // Let the topmost conversation finish paging in its own earlier messages
  // (handled by the thread's observer) before loading older conversations.
  const [topConversation] = conversations.value;
  const topHasEarlier =
    topConversation &&
    messagesCount(topConversation.id) > (topConversation.messages?.length || 0);
  if (!topHasEarlier) loadOlder();
};

const onSelectContact = contact => {
  if (Number(contact.id) === selectedContactId.value) return;
  router.push({
    path: frontendURL(
      `accounts/${route.params.accountId}/interactions/${contact.id}`
    ),
  });
};

const goBackToList = () => {
  router.push({
    path: frontendURL(`accounts/${route.params.accountId}/interactions`),
  });
};

watch(
  selectedContactId,
  newId => {
    if (newId) store.dispatch('contacts/show', { id: newId });
  },
  { immediate: true }
);

onMounted(() => {
  // Message bubbles resolve their inbox by id; make sure inboxes are loaded.
  store.dispatch('inboxes/get');
});
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <div
      class="flex-shrink-0 w-full h-full md:w-80"
      :class="{ 'hidden md:block': selectedContactId }"
    >
      <InteractionsContactList
        :selected-contact-id="selectedContactId"
        @select="onSelectContact"
      />
    </div>

    <div
      class="flex flex-col flex-1 h-full min-w-0 bg-n-surface-1"
      :class="{ 'hidden md:flex': !selectedContactId }"
    >
      <!-- No contact selected (desktop) -->
      <div
        v-if="!selectedContactId"
        class="flex flex-col items-center justify-center flex-1 gap-2 px-6 text-center"
      >
        <span class="i-lucide-messages-square size-10 text-n-slate-8" />
        <p class="text-base font-medium text-n-slate-12">
          {{ t('CONTACT_INTERACTIONS.EMPTY_STATE.TITLE') }}
        </p>
        <p class="max-w-sm text-sm text-n-slate-11">
          {{ t('CONTACT_INTERACTIONS.EMPTY_STATE.SUBTITLE') }}
        </p>
      </div>

      <template v-else>
        <header
          class="flex items-center flex-shrink-0 gap-2 px-4 py-3 border-b md:px-6 border-n-weak"
        >
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-arrow-left"
            class="md:hidden"
            @click="goBackToList"
          />
          <Avatar
            :name="selectedContact.name || ''"
            :src="selectedContact.thumbnail"
            :size="36"
            rounded-full
          />
          <div class="min-w-0">
            <h2 class="text-sm font-medium truncate text-n-slate-12">
              {{ selectedContact.name }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ summaryText }}
            </p>
          </div>
        </header>

        <div
          ref="scrollContainer"
          class="flex-1 overflow-y-auto"
          @scroll="onScroll"
        >
          <div
            v-if="isFetching"
            class="flex items-center justify-center h-full"
          >
            <Spinner />
          </div>

          <div
            v-else-if="hasError"
            class="flex flex-col items-center justify-center h-full gap-3 px-6 text-center"
          >
            <span class="i-lucide-triangle-alert size-8 text-n-slate-9" />
            <p class="text-sm text-n-slate-11">
              {{ t('CONTACT_INTERACTIONS.THREAD.ERROR') }}
            </p>
            <Button
              variant="outline"
              color="slate"
              size="sm"
              :label="t('CONTACT_INTERACTIONS.THREAD.RETRY')"
              @click="loadInitial"
            />
          </div>

          <div
            v-else-if="showEmptyThread"
            class="flex items-center justify-center h-full px-6 text-sm text-center text-n-slate-11"
          >
            {{ t('CONTACT_INTERACTIONS.THREAD.EMPTY') }}
          </div>

          <template v-else>
            <div
              v-if="isLoadingOlder"
              class="flex items-center justify-center py-3"
            >
              <Spinner />
            </div>
            <div
              v-else-if="hasOlderConversations"
              class="flex items-center justify-center py-3"
            >
              <Button
                variant="faded"
                color="slate"
                size="sm"
                :label="t('CONTACT_INTERACTIONS.THREAD.LOAD_EARLIER')"
                @click="loadOlder"
              />
            </div>
            <InteractionThread
              :conversations="conversations"
              :current-user-id="currentUserId || 0"
              :account-id="route.params.accountId"
              :messages-count="messagesCount"
              :is-loading-earlier="isLoadingEarlier"
              :scroll-root="scrollContainer"
              @load-earlier="loadEarlierMessages"
            />
          </template>
        </div>

        <!-- Bottom composer: replies into the contact's most recent open
             conversation via the shared ReplyBox. -->
        <div
          v-if="!isFetching && !hasError && hasConversations"
          class="flex-shrink-0 border-t border-n-weak"
        >
          <template v-if="replyTarget">
            <div
              class="flex items-center gap-1.5 px-4 py-1.5 text-xs text-n-slate-11 bg-n-alpha-black1 md:px-6"
            >
              <span class="i-lucide-corner-up-left size-3.5 text-n-slate-10" />
              {{
                t('CONTACT_INTERACTIONS.COMPOSER.REPLYING_IN', {
                  id: replyTarget.id,
                  inbox: replyTargetInboxName,
                })
              }}
            </div>
            <ReplyBox v-if="isComposerActive" />
            <!-- Lazily activate the active chat only when the user wants to
                 reply, so merely viewing does not mark the conversation read. -->
            <button
              v-else-if="!isActivated"
              type="button"
              class="w-full px-4 py-3 text-sm text-left reset-base text-n-slate-11 hover:bg-n-alpha-black1 md:px-6"
              @click="activate"
            >
              {{
                t('CONTACT_INTERACTIONS.COMPOSER.PLACEHOLDER', {
                  name: selectedContact.name,
                })
              }}
            </button>
          </template>
          <div v-else class="px-6 py-3 text-xs text-center text-n-slate-10">
            {{ t('CONTACT_INTERACTIONS.COMPOSER.NO_OPEN_CONVERSATION') }}
          </div>
        </div>
      </template>
    </div>
  </section>
</template>
